import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import replySubject from "@/components/writeMail/replySubject";
import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import { MailsListId } from "@/domain/entities/emails/Email";
import MailDraft from "@/domain/entities/emails/MailDraft";
import type { AssistantDraft } from "@/domain/entities/vistassistant/AIResponse";
import MailActionType from "@/domain/enums/MailActionType";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, Copy, Send } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cardClassName } from "./assistantClasses";

const buttonClassName =
    "inline-flex h-9 items-center gap-1.5 border border-assistant-outline bg-white px-4 text-sm font-semibold text-primary-darkest transition-colors hover:bg-assistant-chip [&_svg]:size-4 [&_svg]:flex-none";

interface MailDraftCardProps {
    draft: AssistantDraft;
    /** The list of the answer's mails, for the mail the draft answers. */
    listId: MailsListId;
}

/**
 * A mail draft prepared by the assistant (step-based contract), after its answer: subject, text, and
 * two ways out — copy the text, or open it in the composer of the side panel (a reply to the mail it
 * answers, or a new mail without recipient), where the user reviews it and sends it himself. The text is shown as
 * plain text: nothing the model wrote is formatted, linked or loaded.
 */
const MailDraftCard = ({ draft, listId }: MailDraftCardProps) => {
    const { t } = useTranslation();
    const { onMailClick, onComposeDraft } = useVistassistantMail();
    const [copy, setCopy] = useState<"idle" | "copied" | "failed">("idle");
    const bodyRef = useRef<HTMLParagraphElement>(null);
    const { replyTo } = draft;
    const subject = replyTo
        ? replySubject(replyTo.subject, t("email.re"))
        : draft.subject;
    const sender = replyTo?.from?.name || replyTo?.from?.email || "";

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(draft.body);
            setCopy("copied");
            setTimeout(() => setCopy("idle"), 1500);
        } catch {
            // Clipboard refused: the text is selected, for a copy by hand.
            const selection = window.getSelection();
            if (bodyRef.current && selection) {
                selection.selectAllChildren(bodyRef.current);
            }
            setCopy("failed");
        }
    };

    // A new id each time: opening the same draft again fills the composer again.
    const open = (actionType = MailActionType.REPLY) => {
        const mailDraft: MailDraft = {
            id: crypto.randomUUID(),
            body: draft.body,
            ...(replyTo ? {} : { subject: draft.subject ?? "" }),
        };
        // Written in the assistant's side panel, one tab per draft (opened again: refilled).
        onComposeDraft?.(
            replyTo
                ? `reply:${replyTo.uniqueMailvistaId ?? replyTo.id}`
                : `new:${listId}`,
            mailDraft,
            replyTo ? { mail: replyTo, listId, actionType } : undefined,
        );
    };

    return (
        <section
            aria-label={t("vistassistant.draft.label")}
            className={cn(cardClassName, "w-full overflow-hidden")}
        >
            <div className="flex flex-col gap-0.5 border-b border-assistant-cardBorder px-4 py-3 text-[13px]">
                <div className="flex min-w-0 gap-1.5">
                    <span className="flex-none text-assistant-muted">
                        {t("email.subject")} :
                    </span>
                    <span className="truncate font-medium text-primary-darkest">
                        {subject || t("vistassistant.noSubject")}
                    </span>
                </div>
                {replyTo && (
                    <div className="flex min-w-0 gap-1.5 text-assistant-muted">
                        <span className="flex-none">
                            {t("vistassistant.draft.replyTo")}
                        </span>
                        <button
                            className="truncate text-primary-darker hover:underline"
                            onClick={() => onMailClick?.(replyTo, listId)}
                            title={replyTo.subject}
                            type="button"
                        >
                            {sender || replyTo.subject}
                        </button>
                    </div>
                )}
            </div>
            <p
                ref={bodyRef}
                className="whitespace-pre-wrap break-words px-4 py-3 text-base leading-6 text-primary-darkest"
            >
                {draft.body}
            </p>
            <div className="flex flex-wrap items-center justify-end gap-2 px-4 pb-4">
                {copy === "failed" && (
                    <span
                        className="mr-auto text-[13px] text-assistant-muted"
                        role="status"
                    >
                        {t("vistassistant.draft.copyFailed")}
                    </span>
                )}
                <button
                    className={cn(buttonClassName, "rounded-full")}
                    onClick={handleCopy}
                    type="button"
                >
                    {copy === "copied" ? (
                        <Check aria-hidden />
                    ) : (
                        <Copy aria-hidden />
                    )}
                    {copy === "copied"
                        ? t("vistassistant.draft.copied")
                        : t("vistassistant.draft.copy")}
                </button>
                <div className="inline-flex">
                    <button
                        className={cn(
                            buttonClassName,
                            replyTo ? "rounded-l-full" : "rounded-full",
                        )}
                        onClick={() => open()}
                        type="button"
                    >
                        <Send aria-hidden />
                        {t("vistassistant.draft.open")}
                    </button>
                    {replyTo && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    aria-label={t(
                                        "vistassistant.draft.openOptions",
                                    )}
                                    className={cn(
                                        buttonClassName,
                                        "rounded-r-full border-l-0 pl-2 pr-3",
                                    )}
                                    type="button"
                                >
                                    <ChevronDown aria-hidden />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => open()}>
                                    {t("vistassistant.draft.reply")}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onSelect={() =>
                                        open(MailActionType.REPLY_ALL)
                                    }
                                >
                                    {t("vistassistant.draft.replyAll")}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>
        </section>
    );
};

export default MailDraftCard;
